import { computed, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
    Observable,
    filter,
    BehaviorSubject,
    interval,
    debounce,
} from 'rxjs';

import { NotificationCommandProps, NotificationState } from '../../domain/models/notification.models';

@Injectable({ providedIn: 'root' })
export class NotificationService {
    private readonly DEFAULT_TIME_NOTIFICATION: number = 4000;

    private readonly DEFAULT_TIME_ANIMATION: number = 1000;

    private readonly DEFAULT_TIME_ANIMATION_START: number = 100;

    private readonly NOTIFICATION_INITIAL_STATE: NotificationState = {
        status: false,
        props: null,
        canClose: true,
    };

    private readonly state = signal<NotificationState>(this.NOTIFICATION_INITIAL_STATE);

    private readonly _timeOpen: BehaviorSubject<number> = new BehaviorSubject(undefined);

    private readonly timeOpen$: Observable<number> = this._timeOpen.asObservable();

    // Selectors
    public readonly status = computed(() => this.state().status);

    public readonly props = computed(() => this.state().props);

    public readonly type = computed(() => this.state().props?.type);

    constructor() {
        this.timeOpen$.pipe(
            filter((time) => time > 0),
            debounce((time) => interval(time)),
            filter(() => this.state().canClose),
            takeUntilDestroyed()
        ).subscribe(() => {
            this.closeNotification();
        });
    }

    public openNotification(props: NotificationCommandProps): void {
        this.state.update((state) => ({
            ...state,
            props,
        }));

        setTimeout(() => {
            this.updateStatus(true);
            this.updateTimeOpen();
        }, this.DEFAULT_TIME_ANIMATION_START);
    }

    public closeNotification() {
        this.updateStatus(false);

        setTimeout(() => {
            this.resetNotification();
        }, this.DEFAULT_TIME_ANIMATION);
    }

    public resetNotification() {
        this.state.set(this.NOTIFICATION_INITIAL_STATE);
    }

    public updateCanClose(canClose: boolean) {
        this.state.update((state) => ({
            ...state,
            canClose,
        }));
    }

    public updateStatus(status: boolean) {
        this.state.update((state) => ({
            ...state,
            status,
        }));
    }

    public updateTimeOpen(): void {
        this._timeOpen.next(this.props()?.time ?? this.DEFAULT_TIME_NOTIFICATION);
    }

    public clickAction() {
        this.props()?.action();
        this.closeNotification();
    }
}
